import { computed, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import gql from 'graphql-tag'
import { isReference } from '@apollo/client/core'

import {
  FoldersDocument,
  ItemType,
  useAddFolderMutation,
  type FoldersQuery,
} from '@/shared/graphql/legacy/generated'
import { useExplorerStore } from '@/features/explorer/store'
import { routerApolloClient } from '@/shared/graphql/client'

interface AddFolderProps {
  folderId: string
  parentPath?: string
  handler?: (folder: { id: string; name: string }) => void
}

export function useAddFolder(props: AddFolderProps, close: () => void) {
  const route = useRoute()
  const router = useRouter()
  const explorerStore = useExplorerStore()

  const name = ref('')
  const error = ref('')

  const canSave = computed(() => !!name.value.trim().length)

  const { loading, mutate: addFolder } = useAddFolderMutation()

  watch(name, () => {
    error.value = ''
  })

  const resolveExplorerParentId = () => {
    if (props.folderId && props.folderId !== 'root') return props.folderId
    if (explorerStore.currentFolder?.path === '/') {
      return explorerStore.currentFolder.id
    }

    const rootQuery = routerApolloClient.cache.extract()['ROOT_QUERY']
    if (!rootQuery) return undefined

    const rootFolder = Object.entries(rootQuery).find(
      ([key]) => key.startsWith('folderByPath') && key.includes('"path":"/"')
    )?.[1]

    return isReference(rootFolder)
      ? rootFolder.__ref.replace('Folder:', '')
      : undefined
  }

  const addFolderToExplorerCache = (
    folder: { id: string; name: string; timestamp: string },
    parentId: string | undefined,
    parentPath: string
  ) => {
    if (!parentId) return

    routerApolloClient.cache.modify({
      id: routerApolloClient.cache.identify({
        __typename: 'Folder',
        id: parentId,
      }),
      fields: {
        items(existing) {
          const newFolderRef = routerApolloClient.cache.writeFragment({
            fragment: gql`
              fragment NewFolder on Folder {
                __typename
                id
                name
                path
                createdAt
                totalFiles
              }
            `,
            data: {
              __typename: 'Folder',
              id: folder.id,
              name: folder.name,
              path: `${parentPath}/${folder.name}`,
              createdAt: folder.timestamp,
              totalFiles: 0,
            },
          })

          if (!newFolderRef) return existing

          return {
            ...existing,
            edges: [
              { __typename: 'ItemEdge', cursor: folder.id, node: newFolderRef },
              ...existing.edges,
            ],
          }
        },
        totalFiles(existing: number) {
          return existing + 1
        },
      },
    })
  }

  const handleClick = async () => {
    if (!name.value.trim().length) {
      error.value = 'Please enter a folder name.'
      return
    }

    try {
      const response = await addFolder(
        {
          folderName: name.value,
          parentId: props.folderId || 'root',
        },
        {
          update(cache, { data }) {
            const newFolder = data?.createFolder
            if (!newFolder) return

            cache.updateQuery<FoldersQuery>(
              {
                query: FoldersDocument,
                variables: { id: props.folderId || 'root' },
              },
              (foldersResponse) => {
                if (!foldersResponse) return

                return {
                  ...foldersResponse,
                  folders: {
                    ...foldersResponse.folders,
                    items: [
                      {
                        __typename: 'Item' as const,
                        id: newFolder.id,
                        name: newFolder.name,
                        type: ItemType.Folder,
                        parentPath: props.parentPath ?? '',
                      },
                      ...foldersResponse.folders.items,
                    ],
                  },
                }
              }
            )
          },
        }
      )

      const folder = response?.data?.createFolder

      if (!folder) return

      if (props.handler) {
        addFolderToExplorerCache(
          folder,
          resolveExplorerParentId(),
          props.parentPath ?? ''
        )
        props.handler({ id: folder.id, name: folder.name })
        close()
        return
      }

      const explorerPath = explorerStore.currentFolder?.path
      addFolderToExplorerCache(
        folder,
        explorerStore.currentFolder?.id || props.folderId,
        explorerPath === '/' ? '' : (explorerPath ?? '')
      )

      const newFolderPath = `${explorerStore.currentFolder?.path === '/' ? '' : explorerStore.currentFolder?.path}/${folder.name}`

      explorerStore.setNewFolder({
        id: folder.id,
        name: folder.name,
        path: newFolderPath,
        createdAt: folder.timestamp,
      })

      router.push(`${route.path}/${encodeURIComponent(folder.name)}`)

      close()
    } catch (err) {
      if (err instanceof Error) {
        if (err.message.includes('already')) {
          error.value = 'A file or folder with this name is already taken'
        } else if (err.message.includes('invalid')) {
          error.value = err.message
        }
      }
    }
  }

  return { name, error, loading, canSave, handleClick }
}
